import { ReactNode } from 'react';
import { ChevronDown, ChevronUp, HelpCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItemProps {
  label: string;
  icon: LucideIcon;
  badge?: number;
  isActive?: boolean;
  isExpanded?: boolean;
  isCollapsed?: boolean;
  hasSubItems?: boolean;
  helpHref?: string;
  helpLabel?: string;
  onClick?: () => void;
  children?: ReactNode;
}

export function NavItem({
  label,
  icon: Icon,
  badge,
  isActive = false,
  isExpanded = false,
  isCollapsed = false,
  hasSubItems = false,
  helpHref,
  helpLabel = 'Help',
  onClick,
  children
}: NavItemProps) {
  const showHelp = !!helpHref && !isCollapsed;

  return (
    <>
      <div className={cn('relative group/nav', !hasSubItems && 'mt-1')}>
        <button
          onClick={onClick}
          className={cn(
            'w-full flex items-center gap-3 px-2 py-2 rounded-md transition-colors text-sm',
            'hover:bg-accent hover:text-accent-foreground',
            isActive && 'bg-accent text-accent-foreground font-medium',
            !isActive && 'text-foreground/80',
            showHelp && 'pr-8'
          )}>
          <Icon className="w-4 h-4 shrink-0" />
          {!isCollapsed && (
            <>
              <span className="flex-1 text-left truncate">{label}</span>
              {badge && badge > 0 && (
                <span className="bg-destructive text-destructive-foreground px-1.5 py-0.5 text-xs rounded-full">
                  {badge}
                </span>
              )}
              {hasSubItems && (isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
            </>
          )}
        </button>

        {showHelp && (
          <a
            href={helpHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={helpLabel}
            title={helpLabel}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md',
              'text-muted-foreground hover:text-foreground hover:bg-accent',
              'transition-opacity lg:opacity-0 lg:group-hover/nav:opacity-100 lg:focus-visible:opacity-100'
            )}>
            <HelpCircle className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      {!isCollapsed && hasSubItems && isExpanded && children && <ul className="mt-1 ml-4 pl-2">{children}</ul>}
    </>
  );
}
